import { ref } from 'vue'
import Bugsnag from '@bugsnag/js'

import { useFetch } from '@render/composables/useFetch'
import type { PaymentSession } from '@render/composables/paymentSession'

// Shared session-create composable for both Primer and Yuno. Both providers POST
// the same flat `paymentData` payload to `POST /api/v1/payments` (discriminated
// server-side by `paymentData.provider`); only the response shape differs, which
// the caller pins via the generic `T` (defaults to Primer's `PaymentSession`, so
// existing Primer/SavedCard call sites are unchanged). Yuno passes
// `usePaymentSession<YunoCheckoutSession>(...)`.
export default function usePaymentSession<T = PaymentSession>(
  paymentsUrl: string,
  paymentData: {
    [key: string]: string | number | boolean | unknown
  },
) {
  const data = ref<T | null>(null)
  const isLoading = ref<boolean>(false)
  const isError = ref<boolean>(false)
  const { error, post } = useFetch()

  const fetchPaymentSession = async () => {
    isLoading.value = true
    isError.value = false

    try {
      // 30s deadline: this session create sits on the checkout path (Primer /
      // Yuno block on it before rendering the payment form). Pinned explicitly
      // to 30s — matching the prior implicit DEFAULT_TIMEOUT_MS behavior — so
      // this request is deliberately NOT tightened to the shorter pipeorders
      // deadlines; a hung socket still eventually aborts rather than parking
      // the user forever.
      const response = await post(paymentsUrl, { body: paymentData, timeoutMs: 30_000 })

      if (error.value) {
        throw new Error(
          `Error fetching payments: ${error.value.message} with status ${error.value.status}`,
        )
      }

      const session = response as T
      data.value = session
    } catch (error) {
      isError.value = true
      const fetchErr = error as { status?: number; body?: unknown; message?: string } | undefined
      console.error('[payment-session] fetchPaymentSession ← FAIL', {
        status: fetchErr?.status,
        message: fetchErr?.message,
        body: fetchErr?.body,
        sentPayload: paymentData,
      })
      if (Bugsnag.isStarted()) {
        Bugsnag.notify('usePaymentSession: failed to fetch', (event) => {
          event.context = 'usePaymentSession.fetchPaymentSession'
          event.severity = 'error'
          event.addMetadata('payment', { payload: paymentData })
          event.addMetadata('error', {
            message: (error as Error).message,
            stack: (error as Error).stack,
            status: (error as unknown as { status?: number }).status,
            endpoint: paymentsUrl,
          })
        })
      }
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading,
    isError,
    data,
    error,
    fetchPaymentSession,
  }
}
